import React, { useEffect, useState, useCallback } from "react";
import { Drawer, Tag } from "antd";
import { RobotOutlined } from "@ant-design/icons";
import CopilotChat from "./CopilotChat";
import IRDraftPreview from "./IRDraftPreview";
import { useCopilotChatMutation } from "../../Redux/service/Copilot/Copilot";
import { useGetSpecificFormQuery } from "../../Redux/service/Forms/Form";
import { toast } from "react-toastify";
import trans, { isRTL } from "../../utils";
import { C, S, R } from "./copilotTheme";

/**
 * Top-level drawer for AI-assisted IR creation.
 *
 * - Manages messages[] and draft{} on the client (server is stateless).
 * - Renders CopilotChat (left) and IRDraftPreview (right) side by side.
 * - On submit, IRDraftPreview calls the existing useAddIRMutation directly,
 *   so backend behavior is unchanged.
 */
export default function IRCopilotDrawer({ open, onClose, onSubmitted }) {
	const [messages, setMessages] = useState([]);
	const [draft, setDraft] = useState({});
	const [chat, { isLoading }] = useCopilotChatMutation();

	// Fetch the selected form's schema so we know which fields are AI vs UI
	// vs display, and can render the right pane accordingly. We rely on the
	// existing FormDefinition endpoint your platform already has.
	const { data: formResp } = useGetSpecificFormQuery(draft?.form_id, {
		skip: !draft?.form_id,
	});

	// Adapt the existing form's response shape to the simplified shape that
	// IRDraftPreview expects (handledBy markers etc.)
	const formSchema = React.useMemo(() => {
		if (!formResp?.data) return null;
		const fields = formResp.data.formSchema?.fields ?? [];
		return {
			id: formResp.data.id,
			name: formResp.data.name,
			fields: fields.map((f) => ({
				key: f.key,
				name: f.name,
				title: f.title,
				required: Boolean(f?.required?.value),
				inputType: f.inputType ?? null,
				placeholder: f.placeholder ?? null,
				options: f.dropdownOptions ?? f.radioOptions ?? null,
				handledBy:
					f.name === "Header" ||
					f.name === "Paragraph" ||
					f.name === "Image" ||
					f.name === "Hyperlink"
						? "display"
						: f.name === "Uploader" || f.name === "Signature"
						? "ui"
						: "ai",
			})),
		};
	}, [formResp]);

	// Reset state when the drawer is opened fresh
	useEffect(() => {
		if (open) {
			setMessages([]);
			setDraft({});
		}
	}, [open]);

	const handleSend = useCallback(
		async (userMessage) => {
			try {
				const response = await chat({
					messages,
					draft,
					userMessage,
				}).unwrap();
				setMessages(response.messages);
				setDraft(response.draft);
			} catch (err) {
				console.error("[IRCopilot] chat error", err);
				toast.error(
					err?.data?.message ||
						trans("Copilot is unavailable. Please try again."),
					{ autoClose: 1500, rtl: localStorage.getItem("lang") === "ar" },
				);
			}
		},
		[chat, messages, draft],
	);

	const handleReset = () => {
		setMessages([]);
		setDraft({});
	};

	const handleSubmitted = () => {
		setMessages([]);
		setDraft({});
		onSubmitted?.();
		onClose?.();
	};

	return (
		<Drawer
			open={open}
			onClose={onClose}
			width="55%"
			placement={isRTL ? "left" : "right"}
			title={
				<div
					style={{ display: "flex", alignItems: "center", gap: S.sm }}
				>
					<span
						style={{
							width: 30,
							height: 30,
							borderRadius: R.sm,
							background: C.accentSoft,
							border: `1px solid ${C.accentLine}`,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<RobotOutlined style={{ color: C.accent, fontSize: 15 }} />
					</span>
					<span style={{ fontWeight: 600, color: C.ink }}>
						{trans("Create IR with AI Copilot")}
					</span>
					<Tag
						style={{
							marginInlineStart: S.xs,
							margin: 0,
							borderRadius: R.pill,
							background: C.surfaceSunken,
							border: `1px solid ${C.line}`,
							color: C.inkSoft,
							fontSize: 11,
							fontWeight: 600,
							letterSpacing: "0.04em",
						}}
					>
						{trans("Beta")}
					</Tag>
				</div>
			}
			destroyOnClose
			styles={{
				header: { borderBottom: `1px solid ${C.line}` },
				body: { padding: S.md, background: C.surfaceMuted },
			}}
		>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					gap: S.md,
					height: "100%",
				}}
			>
				<div style={{ minHeight: 0, height: "100%" }}>
					<CopilotChat
						messages={messages}
						loading={isLoading}
						onSend={handleSend}
					/>
				</div>
				<div
					style={{
						minHeight: 0,
						height: "100%",
						overflow: "auto",
						padding: S.lg,
						background: C.surface,
						borderRadius: R.lg,
						border: `1px solid ${C.line}`,
					}}
				>
					<IRDraftPreview
						draft={draft}
						formSchema={formSchema}
						onSubmitted={handleSubmitted}
						onReset={handleReset}
					/>
				</div>
			</div>
		</Drawer>
	);
}